import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import 'iconify-icon';
import EneaflixWordmark from './EneaflixWordmark';
import { useGlobalWaLink } from '../contexts/SettingsContext';

const EMAIL = 'contato@ieneagrama.com.br';

const SOCIALS = [
  { icon: 'mdi:instagram', href: 'https://instagram.com/ieneagramabr', label: 'Instagram' },
  { icon: 'mdi:linkedin', href: 'https://www.linkedin.com/school/institutoeneagramabrasil/', label: 'LinkedIn' },
];

const COLUMNS = [
  {
    key: 'solucoes',
    title: 'Soluções',
    links: [
      { to: '/produtos/e-personalidade', label: 'E-Personalidade' },
      { to: '/produtos/e-processual', label: 'E-Processual' },
      { to: '/produtos/e-corpo', label: 'E-Corpo' },
      { to: '/produtos/e-vendas', label: 'E-Vendas' },
      { to: '/produtos/e-got', label: 'E-GOT' },
      { to: '/solucoes-empresariais', label: 'Solicitar In Company' },
    ],
  },
  {
    key: 'institucional',
    title: 'Institucional',
    links: [
      { to: '/equipe', label: 'Sobre o IE' },
      { to: '/equipe', label: 'Equipe' },
      { to: '/franquias', label: 'Franquias' },
      { to: '/agenda', label: 'Agenda' },
      { to: '/eneaflix', label: 'ENEAFLIX', wordmark: true },
      { to: '/blog', label: 'Blog' },
    ],
  },
];

const Marquee = () => (
  <div className="overflow-hidden py-10 text-accent-orange/40 text-xs font-mono uppercase tracking-[0.3em] border-b border-white/10">
    <div className="marquee-track">
      {Array.from({ length: 2 }).flatMap((_, dup) => (
        ['Engenharia Comportamental', 'Metodologia Patenteada', '70.000+ Eneagramados', '3.000+ Empresas', '15+ Anos de Atuação', '41 Unidades no Brasil', 'Instituto Eneagrama']
          .map((label, i) => (
            <span key={`${dup}-${i}`} className="mx-8">• {label}</span>
          ))
      ))}
    </div>
  </div>
);

const ColumnAccordion = ({ col, isOpen, onToggle }) => (
  <div className="border-b border-white/10 md:border-0">
    <button
      type="button"
      onClick={onToggle}
      className="w-full md:cursor-default md:pointer-events-none flex items-center justify-between py-4 md:py-0 text-left"
      data-testid={`footer-col-toggle-${col.key}`}
    >
      <span className="text-xs font-mono uppercase tracking-[0.3em] text-accent-orange">
        {col.title}
      </span>
      <iconify-icon
        icon="solar:alt-arrow-down-linear"
        className={`md:hidden text-lg text-neutral-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
      ></iconify-icon>
    </button>
    <ul className={`${isOpen ? 'block' : 'hidden'} md:block space-y-3 pb-5 md:pb-0 md:mt-5`}>
      {col.links.map((l) => (
        <li key={l.to + l.label}>
          <Link
            to={l.to}
            className="block text-sm text-neutral-400 hover:text-[#EAEAEA] transition-colors py-1 min-h-[28px]"
          >
            {l.wordmark ? <EneaflixWordmark /> : l.label}
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

const Footer = () => {
  const WA_LINK = useGlobalWaLink();
  const currentYear = new Date().getFullYear();
  const [openCol, setOpenCol] = useState(null);

  return (
    <footer className="relative z-10 bg-[#0A0A0A] text-white/80 border-t border-white/10" data-testid="site-footer">
      <Marquee />

      {/* Main grid */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12">
          {/* Brand column */}
          <div className="md:col-span-4 text-center md:text-left">
            <Link to="/" className="inline-block mb-4">
              <img
                src="https://customer-assets.emergentagent.com/job_ie-interactive/artifacts/xbf78gkn_Simbolo_Laranja.png"
                alt="Instituto Eneagrama"
                className="h-12 w-auto mx-auto md:mx-0"
              />
            </Link>
            <p className="text-base text-[#EAEAEA] font-bold mb-1">Instituto Eneagrama</p>
            <p className="text-sm text-neutral-400 mb-6">A Engenharia do Sistema Humano</p>

            {/* Socials */}
            <div className="flex items-center justify-center md:justify-start gap-4">
              {SOCIALS.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  data-testid={`footer-social-${s.label.toLowerCase()}`}
                  className="w-11 h-11 inline-flex items-center justify-center rounded-full bg-[#1A1A1A] border border-white/5 transition-all duration-300 hover:bg-[#1F1208] hover:border-accent-orange/40 hover:-translate-y-0.5"
                  style={{ color: '#777777' }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = '#FF6700'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = '#777777'; }}
                >
                  <iconify-icon icon={s.icon} className="text-xl"></iconify-icon>
                </a>
              ))}
            </div>
          </div>

          {/* Solutions */}
          <div className="md:col-span-3">
            <ColumnAccordion
              col={COLUMNS[0]}
              isOpen={openCol === COLUMNS[0].key}
              onToggle={() => setOpenCol(openCol === COLUMNS[0].key ? null : COLUMNS[0].key)}
            />
          </div>

          {/* Institutional */}
          <div className="md:col-span-2">
            <ColumnAccordion
              col={COLUMNS[1]}
              isOpen={openCol === COLUMNS[1].key}
              onToggle={() => setOpenCol(openCol === COLUMNS[1].key ? null : COLUMNS[1].key)}
            />
          </div>

          {/* Contact */}
          <div className="md:col-span-3">
            <span className="text-xs font-mono uppercase tracking-[0.3em] text-accent-orange mb-5 block">
              Contato
            </span>
            <ul className="space-y-4 text-sm">
              <li>
                <a
                  href={WA_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-whatsapp"
                  className="inline-flex items-center gap-2 text-neutral-300 hover:text-accent-orange transition-colors min-h-[28px]"
                >
                  <iconify-icon icon="mdi:whatsapp" className="text-accent-orange text-lg"></iconify-icon>
                  +55 47 9920-1212
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${EMAIL}`}
                  data-testid="footer-email"
                  className="inline-flex items-center gap-2 text-neutral-300 hover:text-accent-orange transition-colors min-h-[28px]"
                >
                  <iconify-icon icon="solar:letter-linear" className="text-accent-orange text-lg"></iconify-icon>
                  {EMAIL}
                </a>
              </li>
              <li>
                <Link
                  to="/seja-franqueado"
                  className="inline-block text-neutral-400 hover:text-[#EAEAEA] transition-colors min-h-[28px]"
                >
                  Seja um Franqueado →
                </Link>
              </li>
              <li>
                <Link
                  to="/trabalhe-conosco"
                  className="inline-block text-neutral-400 hover:text-[#EAEAEA] transition-colors min-h-[28px]"
                >
                  Trabalhe Conosco →
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Sub-footer */}
      <div className="border-t border-white/10 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="text-[11px] text-neutral-600 font-mono uppercase tracking-widest text-center">
            © {currentYear} Instituto Eneagrama. Todos os direitos reservados.
          </p>
          <span
            className="text-[10px] text-neutral-600 font-mono uppercase tracking-[0.3em] border border-white/10 px-3 py-1 rounded-full"
            data-testid="footer-patent-badge"
          >
            Metodologia Patenteada
          </span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
